/**
 * MixPanel Analytics Service
 *
 * Provides typed event tracking, user identification with enriched properties,
 * and super properties that attach to every event automatically.
 *
 * Gracefully no-ops when VITE_MIXPANEL_TOKEN is not set.
 */
import mixpanel from 'mixpanel-browser'
import type { AnalyticsEventName, AnalyticsUserProperties, AnalyticsSuperProperties } from '../types/analytics'

let initialized = false

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

export function initAnalytics() {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN
    if (!token) return

    const apiBase = import.meta.env.VITE_API_BASE_URL || ''

    mixpanel.init(token, {
        track_pageview: false, // we track page views manually via router
        persistence: 'localStorage',
        // Route through our backend to bypass ad blockers
        api_host: apiBase ? `${apiBase}/mp` : undefined,
    })
    initialized = true
}

// ---------------------------------------------------------------------------
// User Identification
// ---------------------------------------------------------------------------

/**
 * Identify the current user and set people properties.
 * Called on login, signup, and session restore.
 */
export function identifyUser(id: string, props: AnalyticsUserProperties) {
    if (!initialized) return
    mixpanel.identify(id)
    mixpanel.people.set(props)
}

/**
 * Register super properties that are sent with every subsequent event.
 * Call after identifying the user to attach role, tier, etc.
 */
export function registerSuperProperties(props: AnalyticsSuperProperties) {
    if (!initialized) return
    mixpanel.register(props)
}

/**
 * Reset user identity and super properties on logout.
 */
export function resetUser() {
    if (!initialized) return
    mixpanel.reset()
}

// ---------------------------------------------------------------------------
// Event Tracking
// ---------------------------------------------------------------------------

/**
 * Track a named analytics event with optional properties.
 * Event names are typed to the full taxonomy defined in types/analytics.ts.
 */
export function trackEvent(name: AnalyticsEventName, props?: Record<string, unknown>) {
    if (!initialized) return
    mixpanel.track(name, props)
}

/**
 * Track a page view. Called automatically by router.afterEach.
 */
export function trackPageView(path: string, name?: string) {
    if (!initialized) return
    mixpanel.track('Page View', { path, page_name: name })
}
